import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { Box, Button, ButtonGroup, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";

import { useEventCalendar } from "./useEventCalendar";

// User-facing strings
const STRINGS = {
  DAYS_OF_WEEK: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  PART_OF_SERIES: "Part of series:",
} as const;

type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

interface CalendarEvent {
  color: string;
  endTime: string;
  id: string;
  isPendingApproval: boolean;
  isSelected: boolean;
  isUserAttending: boolean;
  startTime: string;
  title: string;
  seriesId?: string;
  series?: {
    id: string;
    name: string;
    description: string;
  };
}

interface EventCalendarProps {
  event: EventFromQuery;
  onRSVP?: (sessionId: string) => void;
  onCancelRSVP?: (sessionId: string) => void;
  onSelectMeetingTime?: (meetingTime: { id: string; startTime: string; endTime: string } | undefined) => void;
  pendingMeetingTimeIds?: ReadonlySet<string>;
  selectedMeetingTimeId?: string;
  selectedDate?: DateTime;
  onSelectDate?: (date: DateTime | undefined) => void;
  currentMonth: DateTime;
  onMonthChange: (month: DateTime) => void;
}

const DAYS_OF_WEEK = STRINGS.DAYS_OF_WEEK;

export const EventCalendar = ({
  event,
  onRSVP,
  onCancelRSVP,
  onSelectMeetingTime,
  pendingMeetingTimeIds,
  selectedMeetingTimeId,
  selectedDate,
  onSelectDate,
  currentMonth,
  onMonthChange,
}: EventCalendarProps) => {
  const theme = useTheme();

  const { calendarEvents, handleDayClick, handleEventClick, handleNext, handlePrevious, handleToday, weeks } =
    useEventCalendar({
      currentMonth,
      event,
      onCancelRSVP,
      onMonthChange,
      onRSVP,
      onSelectDate,
      onSelectMeetingTime,
      pendingMeetingTimeIds,
      selectedDate,
      selectedMeetingTimeId,
      theme,
    });

  const renderEventBox = (e: CalendarEvent) => (
    <Paper
      key={e.id}
      onClick={() => handleEventClick(e)}
      aria-label={`Event at ${DateTime.fromISO(e.startTime).toFormat("t")}`}
      sx={{
        "&:hover": {
          opacity: 1,
          transform: "scale(1.02)",
        },
        backgroundColor: e.color,
        border: e.isSelected ? `2px solid ${theme.palette.secondary.main}` : "none",
        borderRadius: 1,
        boxShadow: e.isSelected ? 4 : 1,
        cursor: "pointer",
        margin: "2px",
        opacity: 0.8,
        padding: "4px",
        position: "relative",
        transform: e.isSelected ? "scale(1.02)" : "none",
        transition: "all 0.2s ease",
      }}
    >
      <Stack spacing={0.5}>
        <Typography
          variant="caption"
          sx={{
            color: e.isUserAttending && !e.isPendingApproval ? "primary.contrastText" : "text.primary",
            display: "block",
            fontWeight: e.isSelected ? "bold" : "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {DateTime.fromISO(e.startTime).toFormat("t")}
        </Typography>
        {e.series && (
          <Typography
            variant="caption"
            sx={{
              color: e.isUserAttending && !e.isPendingApproval ? "primary.contrastText" : "text.secondary",
              display: "block",
              fontSize: "0.7rem",
              fontStyle: "italic",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {e.series.name}
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  const renderDayCell = (date: DateTime) => {
    const isCurrentMonth = date.hasSame(currentMonth, "month");
    const isSelected = selectedDate?.hasSame(date, "day");
    const isSunday = date.weekday === 7; // Sunday is 7 in Luxon
    const isSaturday = date.weekday === 6; // Saturday is 6 in Luxon
    const dayEvents = calendarEvents.filter((e) => {
      const eventStart = DateTime.fromISO(e.startTime);
      return eventStart.hasSame(date, "day");
    });

    // Format date for test ID and ARIA label
    const formattedDate = date.toFormat("yyyy-MM-dd");
    const humanReadableDate = date.toFormat("MMMM d, yyyy");

    return (
      <Box
        key={date.toISO()}
        onClick={() => handleDayClick(date)}
        data-testid={`calendar-day-${formattedDate}`}
        aria-label={humanReadableDate}
        role="button"
        tabIndex={0}
        sx={{
          "&:hover": {
            backgroundColor: isSelected ? "action.hover" : "action.hover",
          },
          backgroundColor: isSelected ? "action.hover" : "transparent",
          borderBottomWidth: 1,
          borderColor: "divider",
          borderLeftWidth: isSunday ? 0 : 1,
          borderRightWidth: isSaturday ? 0 : 1,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          opacity: isCurrentMonth ? 1 : 0.5,
          position: "relative",
          width: "100%",
        }}
      >
        <Typography
          variant="h6"
          data-testid={`calendar-day-number-${formattedDate}`}
          sx={{
            backgroundColor: isSelected ? "primary.main" : "transparent",
            borderRadius: "50%",
            color: isSelected ? "primary.contrastText" : "text.primary",
            fontWeight: isSelected ? "bold" : "normal",
            left: "8px",
            minWidth: "28px",
            padding: "2px",
            position: "absolute",
            textAlign: "center",
            top: "4px",
            zIndex: 1,
          }}
        >
          {date.toFormat("d")}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "auto",
            position: "relative",
            pt: 5,
          }}
        >
          {dayEvents.map((e) => renderEventBox(e))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Calendar Header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: 2,
        }}
      >
        <ButtonGroup size="small">
          <IconButton onClick={handlePrevious}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={handleNext}>
            <ChevronRightIcon />
          </IconButton>
        </ButtonGroup>

        <Typography variant="h6" sx={{ flex: 1 }}>
          {currentMonth.toFormat("MMMM yyyy")}
        </Typography>

        <Button startIcon={<TodayIcon />} onClick={handleToday} size="small">
          Today
        </Button>
      </Stack>

      {/* Calendar Grid */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Stack sx={{ height: "100%", overflow: "auto" }}>
          {/* Week day headers */}
          <Stack
            direction="row"
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderBottom: 1,
              borderColor: "divider",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            {DAYS_OF_WEEK.map((day, index) => (
              <Box
                key={day}
                sx={{
                  borderColor: "divider",
                  borderLeftWidth: index === 0 ? 0 : 0.5,
                  borderRightWidth: index === 6 ? 0 : 0.5,
                  borderStyle: "solid",
                  flex: 1,
                  p: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {day}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Calendar grid */}
          <Box sx={{ display: "grid", flex: 1, gridTemplateRows: "repeat(6, minmax(125px, 1fr))" }}>
            {weeks.map((week, weekIndex) => (
              <Stack
                key={weekIndex}
                direction="row"
                sx={{
                  borderBottom: weekIndex === 5 ? 0 : 1,
                  borderColor: "divider",
                  minHeight: 125,
                }}
              >
                {week.map((day) => renderDayCell(day))}
              </Stack>
            ))}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
